import Store from "@/models/Store";
import StoreSettings from "@/models/StoreSettings";
import Router from "./index"
import Category from "@/models/Category";
import Product from "@/models/Product";

/**
 * Base function to navigate through routers
 * @param routeName 
 * @param params 
 */
function gotToRoute(routeName: string, params?: any, usePush: boolean = false){
    var routeObject = {} as any;
    routeObject.name = routeName;

    if(params != null || typeof params != 'undefined') {
        routeObject.params = params

        if(params.query)
            routeObject.query = params.query

        if(!routeObject.query) {
            routeObject.query = {};
        }
        
        routeObject.query["_r"] = Date.now();
    }    

    return usePush
        ? Router.push(routeObject).catch(err => {}) 
        : Router.replace(routeObject).catch(err => {}) 
}



function goToStoresPage(navigateToDeliveryStoreDirectly: boolean = false) {
    gotToRoute("storesPage", {
        navigateToDeliveryStoreDirectly: navigateToDeliveryStoreDirectly
    });
}

/**
 * Navigate to a store's home page.
 * @param directlyFromStoresList When true, an intermediate replace to the store listing route is awaited first.
 * This is a no-op on the resulting history stack (both steps replace, so no extra entry is added) — it exists
 * purely to force Vue Router to fully settle any transition still in flight (e.g. one triggered by a very recent
 * back/forward navigation) before firing the real navigation, since issuing a single replace() immediately after
 * such a transition can otherwise lose that race and silently fail. This must run unconditionally (not gated on
 * Router's current route) precisely because right after such a race, Router's own currentRoute can itself be
 * stale/unsettled — checking it to decide whether to apply the fix would skip the fix exactly when it's needed.
 */
async function goToStoreHomePage(store: Store | StoreSettings, directlyFromStoresList: boolean = false) {
    if(directlyFromStoresList) {
        // isTransientSettleHop tells Stores.vue this mount is only here to let the router settle
        // (see the comment above) and not a real visit to the store list — without it, Stores.vue
        // runs its own agnostic-model "auto pick a delivery store and navigate" logic on this
        // transient hop, racing/looping with the real navigation just below
        await gotToRoute("storesPage", { navigateToDeliveryStoreDirectly: false, isTransientSettleHop: true });
    }

    return gotToRoute("storeHomePage", {
        storeId: store.id,
        storeDisplayName: store.displayName
    });
}

/**
 * Navigate to a product's details page.
 * @param directlyFromStoresList When true (single-service store flow, which skips the store home page entirely),
 * the store listing page is navigated to first (replacing the current history entry) so that it sits right
 * before this one, then the item is pushed on top — so navigating back from the item lands on the store
 * listing instead of skipping past it. Both steps go through Vue Router's own API (rather than manipulating
 * browser history directly) so Router's internal current-route bookkeeping never gets out of sync.
 */
async function goToProductDetailsPage(store: Store | StoreSettings, product: Product, directlyFromStoresList: boolean = false) {
    if(directlyFromStoresList) {
        // see the isTransientSettleHop comment in goToStoreHomePage above
        await gotToRoute("storesPage", { navigateToDeliveryStoreDirectly: false, isTransientSettleHop: true });
    }

    return gotToRoute("productDetailsPage", {
        storeId: store.id,
        storeDisplayName: store.displayName,
        productId: product.itemId,
        productName: product.name
    }, directlyFromStoresList);
}

function goToProductsByCategory(category: Category) {
    gotToRoute("productsPageByCategory", {
        categoryId: category.id,
        categoryName: category.name
    });
}

function goToProductsBySearch(search: string) {    
    gotToRoute("productsPageBySearch", {
        query: { q: search}
    });
}

function goToCheckoutPage() {    
    gotToRoute("Checkout");
}

function goToOrderSummary(orderId: number) {    
    gotToRoute("OrderSummaryPage", {
        orderId: orderId
    });
}

function goToOrdersPage() {    
    gotToRoute("OrdersPage");
}

function goToProfilePage() {    
    gotToRoute("ProfilePage");
}

function goToWalletPage() {    
    gotToRoute("WalletPage");
}

function goToLoyaltyPage() {    
    gotToRoute("LoyaltyPage");
}

function goToItemsBySectionPage(section: string, index: number) {
    var params = {
        section: section,
        sectionId: index
    } as any;

    gotToRoute("productsBySection", params);   
}


function goToProductsByBanner(campaignId: number, itemsCollectionType: number, campaignName: string) {
    gotToRoute("productsPageByCampaign", {       
        campaignId: typeof campaignId == "undefined" ? itemsCollectionType : campaignId,
        campaignName: campaignName,
        campaignCollectionType: itemsCollectionType,
        title: campaignName,
    })
}


var routerManager = {
    gotToRoute,
    goToStoresPage,
    goToStoreHomePage,
    goToProductDetailsPage,
    goToProductsByCategory,
    goToCheckoutPage,
    goToProductsBySearch,
    goToOrdersPage,
    goToOrderSummary,
    goToProfilePage,
    goToWalletPage,
    goToLoyaltyPage,
    goToItemsBySectionPage,
    goToProductsByBanner
}

export default routerManager;